/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *==================================================
 */
import React from "react";
import { Typography, Grid, Box, Button } from "@material-ui/core";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import QueryBuilderIcon from "@material-ui/icons/QueryBuilder";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { green, orange, red } from "@material-ui/core/colors";
import Pagination from "../components/pagination";
import { currencyFormat, formatterPEN } from "./../helpers/moneyFormat";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import PaginationPageSize from "../components/pagination-page-size";
import OrderDetailsModal from "../components/order/OrderDetailsModal";
import { formatDateString } from "../helpers/date";
import { useOrderHandler } from "../hooks/rest";
import { ORDER_STATE_BY_ID } from "../constants/order";
import { getTimezoneName, isAdminUser, isSellerUser } from "../helpers/user-session";

interface OrderSearchResultsProps {
  listRecords: any;
  getPrev: Function;
  getNext: Function;
  getActions: any;
  orders: any;
  dialogOption: any;
  handleViewDetails: Function;
  sortAction: Function;
  sortOrder: string;
  orderBy: string;
  t: any;
}

const OrderSearchResults: React.FC<any> = ({
  totalOrders,
  page,
  size,
  orders,
  getPrev,
  getNext,
  setSize,
  t,
  setRefetchOrders,
  setShowLoader,
}) => {
  const isSeller = isSellerUser();

  const { getOrdersById } = useOrderHandler();

  const StyledTableCell = withStyles((theme) => ({
    head: {
      backgroundColor: "#E0E0E0",
      color: theme.palette.common.black,
      fontWeight: "bold",
    },
    body: {
      fontSize: 14,
      border: "solid 1px black",
    },
  }))(TableCell);

  const useStyles = makeStyles({
    table: {
      minWidth: 700,
    },
  });

  const statusLabel = (status: number) => {
    switch (status) {
      case 0:
        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            <QueryBuilderIcon style={{ color: orange[700] }} />
            <Typography
              style={{
                marginLeft: "1px",
                fontWeight: 700,
                color: orange[700],
              }}>
              {ORDER_STATE_BY_ID[status]}
            </Typography>
          </Box>
        );

      case 2:
        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CheckCircleOutlineIcon style={{ color: green[700] }} />
            <Typography style={{ marginLeft: "5px", fontWeight: 700, color: green[700] }}>
              {" "}
              {ORDER_STATE_BY_ID[status]}
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CancelOutlinedIcon style={{ color: red[700] }} />
            <Typography
              style={{
                marginLeft: "1px",
                fontWeight: 700,
                color: red[700],
              }}>
              {ORDER_STATE_BY_ID[status]}
            </Typography>
          </Box>
        );

      default:
        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CheckCircleOutlineIcon style={{ color: green[700] }} />
            <Typography style={{ marginLeft: "5px", fontWeight: 700, color: green[700] }}> -</Typography>
          </Box>
        );
    }
  };

  const handlePageSizeChange = (e: any) => {
    setSize(e.target.value);
  };

  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [order, setOrder] = React.useState();

  const timezoneName: string = getTimezoneName();

  const handleOpenDetail = (detailOrder: any) => {
    setShowLoader(true);
    const [respOrdersById] = getOrdersById(detailOrder.orderId);
    respOrdersById
      .then((resp) => {
        setOpen(true);
        setOrder(resp.data.data);
      })
      .catch(() => {
        // TODO ERROR HANDLE
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const showCustomerEmail = (orders: any[]) => {
    let show = false;
    for (let i = 0; i < orders.length; i++) {
      if (!!orders[i].customerEmail) {
        show = true;
        break;
      }
    }
    return show;
  };

  const showTotalAmount = (orders: any[]) => {
    let show = false;
    for (let i = 0; i < orders.length; i++) {
      if (!!orders[i].totalAmount) {
        show = true;
        break;
      }
    }
    return show;
  };

  return (
    <>
      <OrderDetailsModal
        isOpen={open}
        setOpen={setOpen}
        order={order}
        setRefetchOrders={setRefetchOrders}
        timezoneName={timezoneName}
        setShowLoader={setShowLoader}
      />
      <Grid container direction="column" alignItems="stretch" spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h2">{t("OrderManagement.SearchResultTitle")}</Typography>
        </Grid>

        <Grid container direction="column" spacing={2} style={{ marginTop: "20px", padding: "10px" }}>
          <TableContainer component={Paper}>
            <Table className={classes.table} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>NÚMERO DEL PEDIDO</StyledTableCell>
                  <StyledTableCell align="center">FECHA DEL PEDIDO</StyledTableCell>
                  {showCustomerEmail(orders) && <StyledTableCell>CLIENTE</StyledTableCell>}
                  <StyledTableCell align="center">ESTADO</StyledTableCell>
                  {showTotalAmount(orders) && (
                    <>
                      <StyledTableCell align="center">MONTO TOTAL</StyledTableCell>
                    </>
                  )}
                  <StyledTableCell align="right"></StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders?.map((row: any) => (
                  <TableRow key={row.orderId}>
                    <TableCell component="th" scope="row">
                      {row.orderNo}
                    </TableCell>
                    <TableCell align="center">{formatDateString(timezoneName, row.orderDate)}</TableCell>
                    {showCustomerEmail(orders) && (
                      <>
                        <TableCell style={{ textWrap: "nowrap" }}>{row.customerEmail}</TableCell>
                      </>
                    )}
                    <TableCell align="center">{statusLabel(row.status)}</TableCell>
                    {showTotalAmount(orders) && (
                      <>
                        <TableCell align="center">{currencyFormat(row.currency, row.totalAmount)}</TableCell>
                      </>
                    )}
                    <TableCell align="center">
                      <Button type="button" variant="text" onClick={() => handleOpenDetail(row)}>
                        <ZoomInIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {orders?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isSeller ? 3 : 6} align="center">
                      <Typography variant="h6">La búsqueda no ha devuelto ningún resultado</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid container justify="flex-end" alignItems="center" spacing={3}>
          <PaginationPageSize pageSizeValue={size} handlePageSizeChange={handlePageSizeChange} />
          <Grid item>
            {orders && orders.length > 0 && (
              <>
                <Pagination
                  getPrev={getPrev}
                  getNext={getNext}
                  offset={(page - 1) * size}
                  limit={size}
                  count={totalOrders}
                />
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default OrderSearchResults;
